<template>
  <div
    class="order-tracking-uploader min-h-screen flex items-center justify-center bg-gray-50"
  >
    <div class="bg-white shadow-lg rounded-lg px-8 py-10 w-full max-w-xl">
      <h1 class="text-2xl font-bold text-gray-800 mb-6 text-center">
        Order Tracking Uploader
      </h1>
      <div class="mb-6 text-center">
        <label
          class="flex flex-col items-center px-4 py-6 bg-white text-blue-600 rounded-lg shadow-lg tracking-wide uppercase border border-blue-600 cursor-pointer hover:bg-blue-50 transition duration-150"
        >
          <svg
            class="w-8 h-8"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
            />
          </svg>
          <span class="mt-2 text-base leading-normal">Select a CSV file</span>
          <input
            type="file"
            accept=".csv"
            @change="handleFileUpload"
            ref="csvInput"
            class="hidden"
          />
        </label>
      </div>
      <div v-if="error" class="mb-4 text-center">
        <span class="text-red-600 font-medium">{{ error }}</span>
      </div>
      <div v-if="parsedRows.length">
        <h3 class="text-lg font-semibold text-gray-700 mb-2">
          Preview (first 5 rows):
        </h3>
        <div class="overflow-x-auto">
          <table
            class="min-w-full bg-white border border-gray-200 mb-4 rounded-lg shadow-sm"
          >
            <thead class="bg-gray-100">
              <tr>
                <th
                  class="py-2 px-4 text-left font-medium text-gray-700 border-b"
                >
                  Mobile number
                </th>
                <th
                  class="py-2 px-4 text-left font-medium text-gray-700 border-b"
                >
                  Tracking id
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, i) in previewRows"
                :key="i"
                class="hover:bg-gray-50"
              >
                <td class="py-2 px-4 border-b">{{ row.mobileNumber }}</td>
                <td class="py-2 px-4 border-b">{{ row.trackingId }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          @click="submitData"
          :disabled="isSubmitting"
          class="w-full bg-blue-600 text-white font-semibold py-2 px-4 rounded hover:bg-blue-700 transition duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {{ isSubmitting ? "Uploading..." : "Upload Data" }}
        </button>
        <div
          v-if="success"
          class="text-green-600 text-center mt-4 font-semibold"
        >
          Upload successful! (Check console for array)
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "OrderTrackingUploader",
  data() {
    return {
      parsedRows: [],
      error: "",
      isSubmitting: false,
      success: false
    };
  },
  computed: {
    previewRows() {
      return this.parsedRows.slice(0, 5);
    }
  },
  methods: {
    handleFileUpload(event) {
      this.error = "";
      this.success = false;
      this.parsedRows = [];
      const file = event.target.files[0];
      if (!file) return;
      if (!file.name.endsWith(".csv")) {
        this.error = "Please upload a CSV file.";
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        try {
          const text = e.target.result;
          this.parseCSV(text);
        } catch (err) {
          this.error = "Error reading file.";
        }
      };
      reader.onerror = () => {
        this.error = "Cannot read file.";
      };
      reader.readAsText(file);
    },
    parseCSV(text) {
      // Expecting first row: Mobile number,Tracking id
      const lines = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter(line => line.trim().length > 0);

      if (lines.length < 2) {
        this.error = "CSV requires at least a header and one row.";
        this.parsedRows = [];
        return;
      }

      // Parse header and validate exact columns
      const headerLine = lines[0].split(",").map(h => h.trim().toLowerCase());
      if (
        headerLine[0] !== "mobile number" ||
        headerLine[1] !== "tracking id"
      ) {
        this.error = "CSV header should be: Mobile number,Tracking id";
        this.parsedRows = [];
        return;
      }
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        // supporting possible quoted csv values
        const row = this.parseCsvRow(lines[i]);
        if (row.length !== 2) continue;
        data.push({
          mobileNumber: row[0],
          trackingId: row[1]
        });
      }
      if (data.length === 0) {
        this.error = "No valid data rows in CSV.";
      }
      this.parsedRows = data;
      // if you want to see the array immediately after upload (for debug):
      // console.log('Parsed Array:', this.getArray());
    },
    // Very basic CSV row parser (handles simple quoted fields)
    parseCsvRow(line) {
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"' && line[i + 1] === '"') {
          current += '"';
          i++;
        } else if (c === '"') {
          inQuotes = !inQuotes;
        } else if (c === "," && !inQuotes) {
          result.push(current);
          current = "";
        } else {
          current += c;
        }
      }
      result.push(current);
      return result.map(v => v.trim());
    },
    submitData() {
      this.isSubmitting = true;
      this.success = false;
      // Use this.getArray() for the output format you want:
      const outputArray = this.getArray();
      // For demonstration, we'll log the array. Replace this as needed.

      console.log("Array of Mobile number and Tracking id:", outputArray);
      this.$store
        .dispatch("trackingUploader", {
          requestParams: { trackingNumbers: outputArray }
        })
        .then(() => {
          this.success = true;
          this.isSubmitting = false;
          alert(
            "Data uploaded successfully, Message will be sent to the customer"
          );
        })
        .catch(() => {
          this.error = "Error uploading data.";
          this.isSubmitting = false;
        });
    },
    getArray() {
      // returns: [ { mobileNumber: ..., trackingId: ... }, ... ]
      return this.parsedRows.map(row => {
        let mobile = row.mobileNumber ? row.mobileNumber.trim() : "";
        // Add '91' if it does not start with '91' (after removing any leading '+')
        if (mobile) {
          // Remove any leading '+'
          mobile = mobile.replace(/^\+/, "");
          // Add '91' ONLY if not already starting with '91'
          if (!mobile.startsWith("91")) {
            mobile = "91" + mobile;
          }
        }
        return {
          mobileNumber: mobile,
          trackingId: row.trackingId
        };
      });
    }
  }
};
</script>
